import type { DOMAttributes } from '@react-types/shared';
import * as React from 'react';
import { useMemo } from 'react';

import type { HasChildren } from '../../types/components';
import type { BaseFieldProps } from './common/types';

type ErrorMessage = React.ReactNode | Iterable<React.ReactNode>;

export interface CustomFieldPropsValue<
  D extends object = {},
  E extends ErrorMessage = ErrorMessage,
> {
  inputProps: DOMAttributes & D;
  fieldProps: Partial<
    Omit<BaseFieldProps, 'errorMessage'> & {
      errorMessage?: E;
    }
  >;
}

export const CustomFieldPropsContext = React.createContext<CustomFieldPropsValue>({
  inputProps: {},
  fieldProps: {},
});

export function useCustomFieldProps() {
  return React.useContext(CustomFieldPropsContext);
}

export function createCustomFieldPropsProvider<
  T,
  D extends object = {},
  E extends ErrorMessage = ErrorMessage,
>(fn: (props: T) => CustomFieldPropsValue<D, E>) {
  return function FieldPropsProvider(props: T & HasChildren) {
    const { inputProps, fieldProps } = fn(props);
    const value = useMemo(() => ({ inputProps, fieldProps }), [inputProps, fieldProps]);
    return (
      <CustomFieldPropsContext.Provider value={value}>
        {props.children}
      </CustomFieldPropsContext.Provider>
    );
  };
}
